export const COLORS_PALLETE = [
  "#5B9FFF", // soft blue
  "#FFCE73", // warm yellow
  "#9382D9", // lavender purple
  "#FF754C", // bright orange
  "#EE140F", // strong red
  "#01CD8C", // teal green
  "#19504D", // deep teal
  "#783A00", // dark brown
  "#392091", // deep violet
  "#00A8E8", // cyan blue
  "#FFD166", // golden yellow
  "#EF476F", // magenta red
  "#06D6A0", // bright green
  "#118AB2", // turquoise
  "#073B4C", // dark navy
  "#FF9F1C", // amber orange
  "#2EC4B6", // aqua teal
  "#E71D36", // crimson red
  "#9BC53D", // lime green
  "#5C4B51", // muted mauve-brown
];

export const AGGREGATE_BY_OPTIONS = [
  { label: "Team", value: "project_id" },
  { label: "App", value: "app_id" },
];

export const OTHER = "__Other__";
export const PERCENT_PREFIX = "__percent__";
export const isPercentKey = (key: string | number | undefined) =>
  typeof key === "string" ? key.startsWith(PERCENT_PREFIX) : false;
export const getPercentKey = (key: string | number | undefined) => `${PERCENT_PREFIX}${key}`;

// Color manipulation helpers
const HUE_ROTATION_DEGREES = 40;

/**
 * Generates a new color by adjusting the hue of a base color.
 * @param colorIndex - The index of the color to generate.
 * @param palette - The color palette to use.
 * @returns A new color in hex format.
 */
function generateColor(colorIndex: number, palette: string[]): string {
  const cycle = Math.floor(colorIndex / palette.length);
  const baseColor = palette[colorIndex % palette.length];

  if (cycle === 0) {
    return baseColor;
  }

  // Convert hex to HSL
  const r_hex = parseInt(baseColor.slice(1, 3), 16) / 255;
  const g_hex = parseInt(baseColor.slice(3, 5), 16) / 255;
  const b_hex = parseInt(baseColor.slice(5, 7), 16) / 255;

  const max = Math.max(r_hex, g_hex, b_hex);
  const min = Math.min(r_hex, g_hex, b_hex);
  let h = 0,
    s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r_hex:
        h = (g_hex - b_hex) / d + (g_hex < b_hex ? 6 : 0);
        break;
      case g_hex:
        h = (b_hex - r_hex) / d + 2;
        break;
      case b_hex:
        h = (r_hex - g_hex) / d + 4;
        break;
    }
    h /= 6;
  }

  // Adjust hue
  h = (h * 360 + cycle * HUE_ROTATION_DEGREES) % 360;
  h /= 360;

  // Convert HSL to RGB
  let r: number, g: number, b: number;
  if (s === 0) {
    r = g = b = l;
  } else {
    const hue2rgb = (p: number, q: number, t: number) => {
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1 / 6) return p + (q - p) * 6 * t;
      if (t < 1 / 2) return q;
      if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
      return p;
    };
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    r = hue2rgb(p, q, h + 1 / 3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1 / 3);
  }

  // Convert RGB to hex
  const toHex = (c: number) => ("00" + Math.round(c * 255).toString(16)).slice(-2);
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

// Utility functions for creating color maps
export type ColorMapOptions<T = unknown> = {
  startIndex?: number;
  reservedMappings?: Record<string, string>;
  keyExtractor?: (item: T, index: number) => string;
};

/**
 * Creates a color map for an array of items
 * @param items - Array of items to map to colors
 * @param options - Configuration options
 * @returns Record mapping items to colors
 */
export function createColorMap<T>(
  items: T[],
  options: ColorMapOptions<T> = {}
): Record<string, string> {
  const { startIndex = 0, reservedMappings = {}, keyExtractor } = options;
  const colorMap: Record<string, string> = { ...reservedMappings };

  const reservedColors = new Set(Object.values(reservedMappings));
  const availableColors = COLORS_PALLETE.filter(c => !reservedColors.has(c));
  const palette = availableColors.length > 0 ? availableColors : COLORS_PALLETE;

  let colorIndex = startIndex;

  items.forEach((item, index) => {
    const key = keyExtractor ? keyExtractor(item, index) : String(item);
    if (!colorMap[key]) {
      colorMap[key] = generateColor(colorIndex, palette);
      colorIndex++;
    }
  });

  return colorMap;
}

/**
 * Creates a color map from object keys, with support for nested object processing
 * @param data - Array of objects to extract keys from
 * @param options - Configuration options
 * @returns Record mapping keys to colors
 */
export function createColorMapFromObjectKeys<T extends Record<string, unknown>>(
  data: T[],
  options: ColorMapOptions & {
    excludeKeys?: string[];
    nestedKeyExtractor?: (key: string, value: unknown) => string | null;
  } = {}
): Record<string, string> {
  const {
    startIndex = 1,
    reservedMappings = {},
    excludeKeys = ["date"],
    nestedKeyExtractor,
  } = options;

  const colorMap: Record<string, string> = { ...reservedMappings };

  const reservedColors = new Set(Object.values(reservedMappings));
  const availableColors = COLORS_PALLETE.filter(c => !reservedColors.has(c));
  const palette = availableColors.length > 0 ? availableColors : COLORS_PALLETE;

  // Collect all unique identifiers first to ensure consistent ordering
  const identifiers = new Set<string>();

  data?.forEach(item => {
    Object.keys(item).forEach(key => {
      if (excludeKeys.includes(key) || colorMap[key]) return;

      let identifier = key;
      if (nestedKeyExtractor && item[key]) {
        const extractedKey = nestedKeyExtractor(key, item[key]);
        if (extractedKey) {
          identifier = extractedKey;
        }
      }

      if (!colorMap[identifier]) {
        identifiers.add(identifier);
      }
    });
  });

  // Sort identifiers alphabetically to ensure consistent color assignment
  const sortedIdentifiers = Array.from(identifiers).sort();

  // Assign colors to sorted identifiers
  sortedIdentifiers.forEach((identifier, index) => {
    colorMap[identifier] = generateColor(startIndex + index, palette);
  });

  return colorMap;
}
